import React, { useState } from 'react';
import { Modal, Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { RiCloseLine } from 'react-icons/ri';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import BaseButton from '../../BaseButton';
import BaseImage from '../../BaseImage';
import DownloadIcon from '../../../assets/images/reports-icons/pdf-modal-download-icon.svg';

const PatientDetailImageModal = ({ open, onClose, imageUrl, altText, fileName }) => {
    const md = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const [zoomLevel, setZoomLevel] = useState(1); // Manage zoom state

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = altText || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleZoom = (zoomIn) => {
        setZoomLevel(prevZoomLevel => zoomIn ? prevZoomLevel + 0.2 : prevZoomLevel - 0.2);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    width: '90%',
                    height: '95%',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Modal Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: '0 18px',
                        height: '64px',
                        background: '#ECEFF1',
                    }}
                >
                    <Typography variant={md ? 'h2' : 'h4'} color="primary.black">
                        {fileName}
                    </Typography>
                    <IconButton sx={{ color: '#000' }} onClick={onClose}>
                        <RiCloseLine size={md ? 48 : 32} />
                    </IconButton>
                </Box>

                {/* Image Area */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'calc(100% - 128px)', // Adjusting height excluding header and footer
                        overflow: 'auto',
                    }}
                >
                    <img
                        src={imageUrl}
                        alt={altText}
                        style={{
                            transform: `scale(${zoomLevel})`,
                            transition: 'transform 0.3s ease',
                            maxWidth: '100%',
                            maxHeight: '100%',
                        }}
                    />
                </Box>

                {/* Footer with zoom controls and download */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: '7px 18px',
                        height: '64px',
                        background: '#ECEFF1',
                    }}
                >
                    {/* Zoom Controls */}
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                        <IconButton sx={{color: 'primary.black'}} onClick={() => handleZoom(false)} disabled={zoomLevel <= 1}>
                            <ZoomOutIcon sx={{
                                width: {
                                    xs: '32px',
                                    md: '48px'
                                },
                                height: {
                                    xs: '32px',
                                    md: '48px'
                                },
                            }}  />
                        </IconButton>
                        <IconButton sx={{color: 'primary.black'}} onClick={() => handleZoom(true)} disabled={zoomLevel >= 3}>
                            <ZoomInIcon sx={{
                                width: {
                                    xs: '32px',
                                    md: '48px'
                                },
                                height: {
                                    xs: '32px',
                                    md: '48px'
                                },
                            }}/>
                        </IconButton>
                    </Box>

                    {/* Download Button */}
                    <BaseButton
                        buttonType={'primary'}
                        sx={{
                            width: 'fit-content',
                            height: '44px',
                            display: 'flex',
                            position: 'absolute',
                            right: '18px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            gap: {
                                xs: '4px',
                                md: '8px',
                            },
                        }}
                        onClick={handleDownload}
                    >
                        <BaseImage
                            sx={{
                                width: {
                                    xs: '16px',
                                    md: '20px',
                                },
                                height: {
                                    xs: '16px',
                                    md: '20px',
                                },
                                display: 'flex',
                            }}
                            sximage={{ display: 'flex' }}
                            src={DownloadIcon}
                            alt="icon-download"
                        />
                        <Typography variant={md ? 'body' : 'subText'} color="white" mr="2">
                            Download Image
                        </Typography>
                    </BaseButton>
                </Box>
            </Box>
        </Modal>
    );
};

export default PatientDetailImageModal;
